import type { Workflow } from "@/stores/workflowsStore";
import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { fetchWorkflowById, fetchWorkflowByIdOrNull } from "./workflowsApi";
import { workflowsKeys } from "./workflowsQueryKeys";

type WorkflowNameLookup = {
  /** id → workflow name. Referentially stable across renders when unchanged. */
  nameById: Readonly<Record<string, string>>;
  isLoading: boolean;
};

const DETAIL_STALE_MS = 30_000;
const DETAIL_GC_MS = 5 * 60_000;

/**
 * Single workflow by id. Caches under `workflowsKeys.detail(id)` separately
 * from list pages — list items are slim (no `definition`, `triggerSecret`,
 * etc.), and seeding the detail cache with a slim list item would suppress
 * the detail-endpoint refetch via staleTime and leave `useWorkflow(id)`
 * consumers missing the heavy fields. List→name lookups (toasts) instead
 * walk the list cache directly; see `getWorkflowName` in richToasts.tsx.
 * CRUD invalidation flushes both caches via `workflowsKeys.all`.
 */
export function useWorkflow(
  workflowId: string | null | undefined,
  opts?: { enabled?: boolean },
): UseQueryResult<Workflow> {
  return useQuery({
    queryKey: workflowsKeys.detail(workflowId ?? ""),
    queryFn: () => fetchWorkflowById(workflowId as string),
    enabled: (opts?.enabled ?? true) && !!workflowId,
    staleTime: DETAIL_STALE_MS,
    gcTime: DETAIL_GC_MS,
  });
}

/**
 * Batched name lookup for a set of workflow ids. Used by the history page to
 * resolve workflow names for the runs currently on screen without pulling the
 * entire workflow list. Each id gets its own cache entry, so repeat views (or
 * a single id that's already been fetched) are free.
 *
 * Uses `combine` so the returned `nameById` object is referentially stable
 * across renders when the underlying query data hasn't changed. Without this,
 * `useQueries` returns a fresh results array on every render, invalidating
 * any `useMemo` / `useCallback` that depends on it — and if those callbacks
 * are wired into `useEffect` deps that mutate store state, the result is an
 * infinite render loop (React error #185).
 *
 * Uses the OrNull fetcher so archived / cross-org workflows resolve to a
 * placeholder instead of spamming the console with per-render 404 errors.
 */
export function useWorkflowNamesByIds(
  workflowIds: readonly string[],
  opts?: { enabled?: boolean },
): WorkflowNameLookup {
  const enabled = opts?.enabled ?? true;
  return useQueries({
    queries: workflowIds.map(id => ({
      // Distinct key from `useWorkflow`'s `detail` — the name-lookup path
      // returns `null` on 404 while `detail` throws. Sharing the key would
      // let `null` leak back into `useWorkflow` as a success state.
      queryKey: workflowsKeys.nameLookup(id),
      queryFn: () => fetchWorkflowByIdOrNull(id),
      enabled: enabled && !!id,
      staleTime: DETAIL_STALE_MS,
      gcTime: DETAIL_GC_MS,
    })),
    // Plain object, not Map: TanStack's `replaceEqualDeep` only recurses into
    // plain objects/arrays and treats Maps as opaque values, which would
    // produce a new reference every render and defeat the stability contract.
    combine: (results): WorkflowNameLookup => {
      const nameById: Record<string, string> = {};
      workflowIds.forEach((id, i) => {
        const name = results[i]?.data?.name;
        if (name) nameById[id] = name;
      });
      return {
        nameById,
        isLoading: results.some(r => r.isLoading),
      };
    },
  });
}
